import React, { useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { Ellipsis, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { Department, useDeleteDepartmentMutation, useGetDepartmentsQuery } from '../store/slices/departmentSlice';
import { Employee, useGetEmployeesQuery } from '../store/slices/employeeSlice';
import { addNotification, openModal } from '../store/slices/uiSlice';
import {
  ConfirmDialog,
  ErrorState,
  FilterChip,
  KpiGrid,
  KpiTile,
  Meter,
  PageHeader,
  PersonCell,
  SearchField,
  TableCard,
  TableEmptyRow,
  Tag,
  Toolbar,
  useDelayedFlag,
} from '../components/ui';
import { sbg } from '../theme/sbgTokens';
import { formatCount, formatDate, formatNumber, formatPercent, formatTime, MISSING } from '../utils/format';
import { apiErrorMessage, failureReason } from '../utils/requestErrors';
import { getTodayEt } from '../utils/easternTime';
import { getEmployeeElapsedFraction } from '../utils/timeOffUsage';
import ResponsiveSkeletonRows, { SkeletonColumn } from './departments-parts/ResponsiveSkeletonRows';
import { labelSlotWidth } from './departments-parts/labelSlot';

/* -------------------------------------------------------------------------- */
/* Presentational helpers                                                     */
/* -------------------------------------------------------------------------- */

type SortKey = 'name' | 'headcount' | 'pto';
type SortDirection = 'asc' | 'desc';

interface PtoTotals {
  used: number;
  allotment: number;
}

/*
 * Columns per width. Content is 358px on a phone and about 480px at 768px
 * (beside the sidebar), so the table drops columns rather than scrolling:
 * the manager moves under the department name below 1024px, PTO appears
 * from 640px and Created from 1280px. The ⋯ menu is always on screen.
 */
const HIDE_BELOW_SM = { display: { xs: 'none', sm: 'table-cell' } } as const;
const HIDE_BELOW_LG = { display: { xs: 'none', lg: 'table-cell' } } as const;
const HIDE_BELOW_XL = { display: { xs: 'none', xl: 'table-cell' } } as const;

const SKELETON_COLUMNS: SkeletonColumn[] = [
  { key: 'department' },
  { key: 'manager', sx: HIDE_BELOW_LG },
  { key: 'people', align: 'right', width: 24 },
  { key: 'pto', sx: HIDE_BELOW_SM },
  { key: 'created', sx: HIDE_BELOW_XL },
  { key: 'actions', align: 'right', width: 24 },
];
const COLUMN_COUNT = SKELETON_COLUMNS.length;

/** "Couldn’t delete Archive." + why + what to do. A 404 means someone else deleted it first. */
const deleteErrorMessage = (name: string, err: unknown) => {
  const status = (err as { status?: unknown } | null)?.status;
  if (status === 404) return `Couldn’t delete ${name}. It may already be deleted. Refresh the list to check.`;
  // The API checks membership atomically: someone was assigned after this page loaded.
  if (status === 409) {
    return apiErrorMessage(err) ?? `Couldn’t delete ${name}. People are still assigned to it. Move them to another department first.`;
  }
  // The API refuses deletes until its database update is applied; say so instead of "try again".
  if (status === 503) return `Couldn’t delete ${name}. ${apiErrorMessage(err) ?? 'Deleting departments is unavailable right now.'}`;
  return `Couldn’t delete ${name}. ${failureReason(err)}`;
};

/* Stable stand-ins while a list hasn't arrived. Figures never read these; they wait for the real list. */
const NO_DEPARTMENTS: Department[] = [];
const NO_EMPLOYEES: Employee[] = [];

/* A KPI caption is swapped for a skeleton while its tile loads; this only reserves that line. */
const PENDING_CAPTION = 'Loading';

/** Matches the Employees page: no status on file counts as active. */
const isActive = (employee: Employee) => !employee.employmentStatus || employee.employmentStatus === 'active';

/**
 * PTO used against allotment (hours) for everyone whose record names the
 * department. The department page's figures come from the same set of people.
 */
const sumPto = (people: Employee[]): PtoTotals =>
  people.reduce<PtoTotals>(
    (totals, person) => ({
      used: totals.used + (Number(person.ptoUsed) || 0),
      allotment: totals.allotment + (Number(person.ptoAllotment) || 0),
    }),
    { used: 0, allotment: 0 },
  );

const ptoShare = (totals: PtoTotals | undefined) =>
  totals && totals.allotment > 0 ? totals.used / totals.allotment : null;

/** Hours with a unit: 48h · 12.5h · — */
const formatHours = (hours: number | null | undefined) => {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return MISSING;
  return `${formatNumber(hours, Number.isInteger(hours) ? 0 : 1)}h`;
};

/** "96 of 240h" */
const hoursOf = (used: number, total: number) =>
  `${formatNumber(used, Number.isInteger(used) ? 0 : 1)} of ${formatHours(total)}`;

/** "40%" for a usage share (0.4). */
const sharePercent = (share: number) => formatPercent(share * 100);

/** "Archive" · "Archive and Ops" · "Archive, Ops and 3 more" */
const listNames = (names: string[]) => {
  if (names.length <= 2) return names.join(' and ');
  return `${names[0]}, ${names[1]} and ${formatNumber(names.length - 2)} more`;
};

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

const Departments: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState('');
  const [salesOnly, setSalesOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  // `deleteTarget` outlives `deleteOpen` so the dialog keeps its words while it fades out.
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deletingRef = useRef(false);
  const [deleteDepartment] = useDeleteDepartmentMutation();

  const {
    data: departmentsData,
    isLoading,
    error,
    refetch,
    fulfilledTimeStamp,
  } = useGetDepartmentsQuery();
  const {
    data: employeesData,
    isLoading: employeesLoading,
    isError: employeesError,
    refetch: refetchEmployees,
  } = useGetEmployeesQuery();
  const departments = departmentsData ?? NO_DEPARTMENTS;
  const employees = employeesData ?? NO_EMPLOYEES;

  /* A list is pending until it has arrived or failed; until then nothing is counted from it. */
  const departmentsPending = isLoading || (departmentsData === undefined && !error);
  const employeesPending = employeesLoading || (employeesData === undefined && !employeesError);
  /*
   * The KPI row reads both lists and resolves as one: every tile shows its
   * skeleton until both have arrived (or failed), so a final figure such as
   * "0 people" never sits beside tiles that are still loading.
   */
  const kpisPending = departmentsPending || employeesPending;
  /* Cell skeletons for headcount and PTO, revealed after 300ms so fast loads don't flash. */
  const showCellSkeletons = useDelayedFlag(employeesPending, 300);

  /* People grouped by department id, so each row reads its members once. */
  const membersByDepartment = useMemo(() => {
    const map = new Map<string, Employee[]>();
    employees.forEach((employee) => {
      const departmentId = employee.department?._id;
      if (!departmentId) return;
      const list = map.get(departmentId);
      if (list) list.push(employee);
      else map.set(departmentId, [employee]);
    });
    return map;
  }, [employees]);

  const getEmployeeCount = (departmentId: string) => membersByDepartment.get(departmentId)?.length ?? 0;

  /**
   * Why a department can't be deleted yet, or null when it can. DELETE
   * /departments/:id removes the row whoever is in it, and the database then
   * clears each member's department (ON DELETE SET NULL), which drops them from
   * department-based manager scope and sales-team lead routing. So the page
   * only deletes a department with nobody on record, active or former: the
   * same people the People column counts.
   */
  const deleteBlockReason = (department: Department | null): string | null => {
    if (!department) return null;
    if (employeesError) return 'Employee records didn’t load, so who’s in it can’t be checked';
    if (employeesPending) return 'Checking who’s in this department…';
    const people = getEmployeeCount(department._id);
    return people > 0 ? `Move its ${formatCount(people, 'person', 'people')} to another department first` : null;
  };

  const ptoByDepartment = useMemo(() => {
    const map = new Map<string, PtoTotals>();
    membersByDepartment.forEach((people, departmentId) => map.set(departmentId, sumPto(people)));
    return map;
  }, [membersByDepartment]);

  /*
   * The PTO meter's figures sit in fixed, right-aligned slots sized to the
   * longest figures in the list, so every row's track starts and ends at the
   * same x. The percent slot gets extra room for the wide "%" glyph.
   */
  const ptoSlots = useMemo(() => {
    const percents: string[] = [];
    const hours: string[] = [];
    departments.forEach((department) => {
      const pto = ptoByDepartment.get(department._id);
      const share = ptoShare(pto);
      if (!pto || share === null) return;
      percents.push(sharePercent(share));
      hours.push(hoursOf(pto.used, pto.allotment));
    });
    return { percent: labelSlotWidth(percents, 1), hours: labelSlotWidth(hours) };
  }, [departments, ptoByDepartment]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredDepartments = useMemo(
    () =>
      departments.filter(
        (department) =>
          (!salesOnly || Boolean(department.isSalesTeam)) &&
          (department.name.toLowerCase().includes(normalizedSearch) ||
            (department.manager ?? '').toLowerCase().includes(normalizedSearch))
      ),
    [departments, salesOnly, normalizedSearch]
  );

  const sortedDepartments = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    const value = (department: Department) => {
      if (sortKey === 'headcount') return membersByDepartment.get(department._id)?.length ?? 0;
      if (sortKey === 'pto') return ptoShare(ptoByDepartment.get(department._id)) ?? -1;
      return 0;
    };
    return [...filteredDepartments].sort((a, b) => {
      if (sortKey !== 'name') {
        const diff = value(a) - value(b);
        if (diff !== 0) return diff * direction;
      }
      return a.name.localeCompare(b.name) * (sortKey === 'name' ? direction : 1);
    });
  }, [filteredDepartments, sortKey, sortDirection, membersByDepartment, ptoByDepartment]);

  const pagedDepartments = sortedDepartments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const salesTeamCount = departments.filter((department) => department.isSalesTeam).length;
  const hasFilters = normalizedSearch.length > 0 || salesOnly;

  /*
   * Figures for the KPI row: the two gaps to fix, and PTO use against the year.
   * Null until both lists are in, so no tile can show a count of a list that
   * hasn't arrived.
   */
  const summary = useMemo(() => {
    if (kpisPending) return null;
    const unassigned = employees.filter((employee) => isActive(employee) && !employee.department?._id).length;
    const managerless = departments
      .filter((department) => !department.manager?.trim())
      .map((department) => department.name)
      .sort((a, b) => a.localeCompare(b));
    const companyPto = sumPto(employees.filter((employee) => Boolean(employee.department?._id)));
    const todayEt = getTodayEt();
    const year = Number(todayEt.slice(0, 4));
    return {
      unassigned,
      managerless,
      ptoShare: ptoShare(companyPto),
      yearElapsed: getEmployeeElapsedFraction(year, todayEt),
      year,
    };
  }, [kpisPending, employees, departments]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection(key === 'name' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSalesOnly(false);
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, department: Department) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedDepartment(department);
  };

  // The department stays selected until the next open, so the closing menu
  // doesn't redraw its items (and the delete reason) for no department.
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddDepartment = () => {
    dispatch(
      openModal({
        type: 'department-form',
        data: { isEdit: false },
      })
    );
  };

  const handleEditDepartment = (department: Department) => {
    handleMenuClose();
    dispatch(
      openModal({
        type: 'department-form',
        data: { department, isEdit: true },
      })
    );
  };

  const handleViewDepartment = (department: Department) => {
    navigate(`/departments/${department._id}`);
  };

  const handleDeleteDepartment = (department: Department) => {
    handleMenuClose();
    if (deleteBlockReason(department)) return;
    setDeleteError(null);
    setDeleteTarget(department);
    setDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingRef.current) return;
    setDeleteOpen(false);
  };

  /** DELETE /departments/:id (admin/HR). A failure keeps the dialog open with the reason. */
  const confirmDeleteDepartment = async () => {
    if (!deleteOpen || !deleteTarget || deletingRef.current || deleteBlockReason(deleteTarget)) return;
    const { _id: departmentId, name } = deleteTarget;
    deletingRef.current = true;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDepartment(departmentId).unwrap();
      dispatch(addNotification({ type: 'success', message: `Deleted the ${name} department.` }));
      setDeleteOpen(false);
    } catch (err) {
      // A 409 (people were assigned meanwhile) still invalidates the employee
      // list through the slice, so the counts and the delete rule catch up.
      setDeleteError(deleteErrorMessage(name, err));
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  const sortableHead = (key: SortKey, label: string, align: 'left' | 'right' = 'left') => (
    <TableSortLabel
      active={sortKey === key}
      direction={sortKey === key ? sortDirection : key === 'name' ? 'asc' : 'desc'}
      onClick={() => handleSort(key)}
      sx={align === 'right' ? { flexDirection: 'row-reverse', '& .MuiTableSortLabel-icon': { margin: '0 4px 0 0' } } : undefined}
    >
      {label}
    </TableSortLabel>
  );

  const renderPto = (department: Department) => {
    const pto = ptoByDepartment.get(department._id);
    const share = ptoShare(pto);
    if (employeesError || share === null || !pto) {
      return (
        <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
          {MISSING}
        </Typography>
      );
    }
    const overBy = pto.used - pto.allotment;
    const over = overBy > 0;
    /*
     * Track, then the percent and the hours in fixed, right-aligned slots (the
     * same width on every row), so every track starts and ends at the same x.
     * Below 1024px the hours drop under the track. An overage gets its own line.
     */
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(48px, 1fr) auto', lg: 'minmax(48px, 1fr) auto auto' },
          alignItems: 'center',
          columnGap: 1.25,
          rowGap: 0.25,
        }}
      >
        <Meter
          value={pto.used}
          max={pto.allotment}
          tone={over ? 'danger' : 'brand'}
          aria-label={`${department.name}: ${hoursOf(pto.used, pto.allotment)} PTO used${over ? `, over by ${formatHours(overBy)}` : ''}`}
        />
        <Typography
          variant="labelSm"
          component="span"
          sx={{
            width: ptoSlots.percent,
            textAlign: 'right',
            whiteSpace: 'nowrap',
            color: sbg.text.primary,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {sharePercent(share)}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          sx={{
            gridColumn: { xs: '1 / -1', lg: 'auto' },
            width: { xs: 'auto', lg: ptoSlots.hours },
            textAlign: { xs: 'left', lg: 'right' },
            whiteSpace: 'nowrap',
            color: sbg.text.secondary,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {hoursOf(pto.used, pto.allotment)}
        </Typography>
        {over && (
          <Typography
            variant="body2"
            component="span"
            sx={{
              gridColumn: '1 / -1',
              justifySelf: { xs: 'start', lg: 'end' },
              whiteSpace: 'nowrap',
              color: sbg.status.danger.text,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {`Over by ${formatHours(overBy)}`}
          </Typography>
        )}
      </Box>
    );
  };

  const renderBody = () => {
    if (departmentsPending) return <ResponsiveSkeletonRows columns={SKELETON_COLUMNS} rows={8} />;
    if (error) {
      return (
        <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
          <TableCell colSpan={COLUMN_COUNT} sx={{ borderBottom: 0 }}>
            <ErrorState
              compact
              title="Couldn’t load departments"
              body="The server didn’t respond. Try again in a minute; your search is kept."
              onRetry={() => { refetch(); }}
            />
          </TableCell>
        </TableRow>
      );
    }
    if (departments.length === 0) {
      return (
        <TableEmptyRow
          colSpan={COLUMN_COUNT}
          title="No departments yet"
          body="Departments group people for approvals, time off and reports. Add the first one to start."
          action={
            <Button variant="outlined" startIcon={<Plus size={16} />} onClick={handleAddDepartment}>
              Add department
            </Button>
          }
        />
      );
    }
    if (sortedDepartments.length === 0) {
      const reason = normalizedSearch
        ? `Nothing matches “${searchTerm.trim()}”${salesOnly ? ' among sales teams' : ''}.`
        : 'No department is marked as a sales team.';
      return (
        <TableEmptyRow
          colSpan={COLUMN_COUNT}
          title="No departments match these filters"
          body={`${reason} Search looks at department and manager names.`}
          action={<Button variant="outlined" onClick={clearFilters}>Clear filters</Button>}
        />
      );
    }

    return pagedDepartments.map((department) => {
      const employeeCount = getEmployeeCount(department._id);
      const manager = department.manager?.trim();
      return (
        <TableRow
          key={department._id}
          onClick={() => handleViewDepartment(department)}
          sx={{ cursor: 'pointer' }}
        >
          <TableCell sx={{ minWidth: { sm: 160 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5, minWidth: 0 }}>
              <Link
                component={RouterLink}
                to={`/departments/${department._id}`}
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
                underline="hover"
                variant="labelMd"
                sx={{ color: sbg.text.primary, '&:hover': { color: sbg.text.link } }}
              >
                {department.name}
              </Link>
              {department.isSalesTeam && <Tag sx={{ height: 20, px: 1 }}>Sales</Tag>}
            </Box>
            {/* Below 1024px the Manager column is hidden, so the manager reads here. */}
            <Typography
              variant="body2"
              component="div"
              sx={{ display: { xs: 'block', lg: 'none' }, color: sbg.text.secondary, mt: 0.25 }}
            >
              {manager ? `Managed by ${manager}` : 'No manager assigned'}
            </Typography>
          </TableCell>
          <TableCell sx={{ ...HIDE_BELOW_LG, minWidth: 160 }}>
            {manager ? (
              <PersonCell name={manager} size={24} />
            ) : (
              <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
                No manager assigned
              </Typography>
            )}
          </TableCell>
          <TableCell align="right">
            {employeesError ? (
              MISSING
            ) : employeesPending ? (
              <Skeleton
                variant="rounded"
                width={24}
                height={14}
                sx={{ ml: 'auto', visibility: showCellSkeletons ? 'visible' : 'hidden' }}
              />
            ) : (
              formatNumber(employeeCount)
            )}
          </TableCell>
          <TableCell sx={{ ...HIDE_BELOW_SM, minWidth: 152 }}>
            {employeesPending ? (
              <Skeleton
                variant="rounded"
                height={14}
                width="80%"
                sx={{ visibility: showCellSkeletons ? 'visible' : 'hidden' }}
              />
            ) : (
              renderPto(department)
            )}
          </TableCell>
          <TableCell sx={{ ...HIDE_BELOW_XL, color: sbg.text.secondary, whiteSpace: 'nowrap' }}>
            {formatDate(department.createdAt)}
          </TableCell>
          <TableCell align="right" sx={{ width: 56 }}>
            <Tooltip title="More actions">
              <IconButton
                size="small"
                aria-label={`Actions for ${department.name}`}
                aria-haspopup="menu"
                onClick={(e) => handleMenuOpen(e, department)}
              >
                <Ellipsis size={18} />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  /* ---- KPI row ------------------------------------------------------------ */
  /* `summary` is null until both lists are in; the tiles show skeletons for value and caption until then. */

  const unassignedValue = !summary
    ? null
    : employeesError
      ? MISSING
      : formatCount(summary.unassigned, 'person', 'people');
  const unassignedCaption = !summary ? PENDING_CAPTION : employeesError ? (
    <>
      Employee records didn’t load.{' '}
      <Link component="button" type="button" variant="body2" onClick={() => { refetchEmployees(); }} sx={{ verticalAlign: 'baseline' }}>
        Try again
      </Link>
    </>
  ) : summary.unassigned > 0 ? (
    <Link component={RouterLink} to="/employees" variant="body2" underline="hover">
      Assign them on Employees
    </Link>
  ) : (
    'Every active person has one'
  );

  const managerlessValue = !summary ? null : error ? MISSING : formatCount(summary.managerless.length, 'department');
  const managerlessCaption = !summary
    ? PENDING_CAPTION
    : error
      ? 'Departments didn’t load'
      : summary.managerless.length > 0
        ? listNames(summary.managerless)
        : 'Every department has one';

  const ptoValue = !summary
    ? null
    : employeesError || summary.ptoShare === null
      ? MISSING
      : sharePercent(summary.ptoShare);
  const ptoCaption = !summary
    ? PENDING_CAPTION
    : `Of ${summary.year} allotment · ${formatPercent(summary.yearElapsed * 100)} of the year elapsed`;

  const selectedMenuBlockReason = deleteBlockReason(selectedDepartment);
  const deleteTargetBlockReason = deleteBlockReason(deleteTarget);

  return (
    <Box>
      <PageHeader
        overline="Workspace"
        title="Departments"
        meta={[
          departmentsPending ? 'Loading departments…' : error ? null : formatCount(departments.length, 'department'),
          salesTeamCount > 0 ? formatCount(salesTeamCount, 'sales team') : null,
          fulfilledTimeStamp ? `Updated ${formatTime(fulfilledTimeStamp)}` : null,
        ]}
        actions={
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={handleAddDepartment}>
            Add department
          </Button>
        }
      />

      {/* Three across only where each tile has room for its label; stacked below 1024px. */}
      <KpiGrid columns={3} sx={{ gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' } }}>
        <KpiTile
          label="No department"
          loading={kpisPending}
          value={unassignedValue}
          caption={unassignedCaption}
          info="Active people whose employee record has no department."
        />
        <KpiTile
          label="No manager"
          loading={kpisPending}
          value={managerlessValue}
          caption={managerlessCaption}
          info="Departments with no manager set. Choose one with Edit department."
        />
        <KpiTile
          label="PTO used"
          loading={kpisPending}
          value={ptoValue}
          caption={ptoCaption}
          info="PTO hours used this calendar year by everyone whose employee record names a department, as a share of their allotted hours. Each department’s page counts the same people."
        />
      </KpiGrid>

      <TableCard
        aria-label="Departments"
        toolbar={
          <Toolbar
            end={
              !departmentsPending && !error && hasFilters ? (
                <Typography
                  variant="body2"
                  role="status"
                  sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}
                >
                  {`Showing ${formatNumber(sortedDepartments.length)} of ${formatCount(departments.length, 'department')}`}
                </Typography>
              ) : undefined
            }
          >
            <SearchField
              value={searchTerm}
              onChange={(value) => { setSearchTerm(value); setPage(0); }}
              label="Search departments"
              placeholder="Search by department or manager"
              width={300}
            />
            {salesTeamCount > 0 && (
              <FilterChip
                selected={salesOnly}
                onClick={() => { setSalesOnly((current) => !current); setPage(0); }}
                count={salesTeamCount}
              >
                Sales teams
              </FilterChip>
            )}
          </Toolbar>
        }
        footer={
          !departmentsPending && !error && sortedDepartments.length > 0 ? (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={sortedDepartments.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ) : undefined
        }
      >
        <Table aria-label="Departments">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortKey === 'name' ? sortDirection : false}>
                {sortableHead('name', 'Department')}
              </TableCell>
              <TableCell sx={HIDE_BELOW_LG}>Manager</TableCell>
              <TableCell align="right" sortDirection={sortKey === 'headcount' ? sortDirection : false}>
                {sortableHead('headcount', 'People', 'right')}
              </TableCell>
              <TableCell sx={HIDE_BELOW_SM} sortDirection={sortKey === 'pto' ? sortDirection : false}>
                {sortableHead('pto', 'PTO used this year')}
              </TableCell>
              <TableCell sx={HIDE_BELOW_XL}>Created</TableCell>
              <TableCell align="right">
                <span className="sbg-sr-only">Actions</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </Table>
      </TableCard>

      {/* Row actions. Disabled items stay reachable by arrow key so their reason is heard. */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ list: { disabledItemsFocusable: true } }}
      >
        <MenuItem onClick={() => selectedDepartment && handleViewDepartment(selectedDepartment)}>
          <ListItemIcon>
            <Eye size={16} />
          </ListItemIcon>
          View department
        </MenuItem>
        <MenuItem onClick={() => selectedDepartment && handleEditDepartment(selectedDepartment)}>
          <ListItemIcon>
            <Pencil size={16} />
          </ListItemIcon>
          Edit department
        </MenuItem>
        <Divider />
        {/* Only an empty department can be deleted; otherwise the item stays visible with the reason. */}
        <MenuItem
          disabled={Boolean(selectedMenuBlockReason)}
          onClick={() => selectedDepartment && handleDeleteDepartment(selectedDepartment)}
          sx={{ alignItems: 'flex-start', ...(selectedMenuBlockReason ? null : { color: sbg.status.danger.text }) }}
        >
          <ListItemIcon sx={{ color: 'inherit', mt: 0.25 }}>
            <Trash2 size={16} />
          </ListItemIcon>
          <ListItemText
            primary="Delete department"
            secondary={selectedMenuBlockReason}
            slotProps={{
              primary: { variant: 'body2', sx: { color: 'inherit' } },
              secondary: { variant: 'caption', sx: { color: sbg.text.secondary } },
            }}
            sx={{ my: 0 }}
          />
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={deleteOpen && Boolean(deleteTarget)}
        onClose={closeDeleteDialog}
        onConfirm={confirmDeleteDepartment}
        title={deleteTarget ? `Delete the ${deleteTarget.name} department?` : ''}
        body={
          deleteTarget
            ? `Nobody is assigned to ${deleteTarget.name}, so no employee records change. It’s removed from department lists, filters and forms. This can’t be undone.`
            : undefined
        }
        confirmLabel="Delete department"
        loadingLabel="Deleting…"
        destructive
        loading={deleting}
        confirmDisabled={Boolean(deleteTargetBlockReason)}
      >
        {/* The employee list can refresh while the dialog is open; someone may have joined since. */}
        {deleteTargetBlockReason && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {`${deleteTargetBlockReason}.`}
          </Alert>
        )}
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}
      </ConfirmDialog>
    </Box>
  );
};

export default Departments;
